import React from 'react';
import { useData, useField } from '../EpcrDataContext';
import { Icons } from '../EpcrIcons';
import { TextField, OptionField, AutoField, Section, ViewLayout } from '../EpcrFields';

export function EpcrViewBilling() {
  const sections = [
    { id: "payment", label: "Payment" },
    { id: "contact-pay", label: "Contact for Payment" },
    { id: "billing", label: "Billing Details" },
    { id: "necessity", label: "Medical Necessity" },
    { id: "transport", label: "Transport" },
    { id: "work", label: "Work Related" },
    { id: "nok", label: "Next of Kin" },
    { id: "consumables", label: "Consumables" },
  ];
  return (
    <ViewLayout sections={sections}>
      <Section id="payment" num="01" title="Payment" meta="eBilling.method">
        <div className="fgrid">
          <div className="fcol-6"><OptionField label="Primary Method of Payment" elem="eBilling.01" path="eBilling/eBilling.01" /></div>
          <div className="fcol-6"><TextField label="Insurance Group / Plan Name" path="eBilling/eBilling.PlanName" /></div>
          <div className="fcol-6"><TextField label="Policy ID" path="eBilling/eBilling.PolicyID" /></div>
          <div className="fcol-6"><TextField label="Group ID" path="eBilling/eBilling.GroupID" /></div>
        </div>
      </Section>

      <Section id="contact-pay" num="02" title="Contact for Payment" meta="eBilling.contact">
        <div className="fgrid">
          <div className="fcol-4"><TextField label="Last Name" path="eBilling/eBilling.ContactLast" /></div>
          <div className="fcol-4"><TextField label="First Name" path="eBilling/eBilling.ContactFirst" /></div>
          <div className="fcol-4"><TextField label="Relationship" path="eBilling/eBilling.ContactRelation" /></div>
          <div className="fcol-8"><TextField label="Address" path="eBilling/eBilling.ContactAddress" /></div>
          <div className="fcol-4"><TextField label="Phone" path="eBilling/eBilling.ContactPhone" /></div>
        </div>
      </Section>

      <Section id="billing" num="03" title="Billing Details" meta="agency rates">
        <div className="fgrid">
          <div className="fcol-12">
            <TextField label="Special Billing Instructions" path="eBilling/eBilling.Notes" multiline rows={3} />
          </div>
        </div>
      </Section>

      <Section id="necessity" num="04" title="Medical Necessity" meta="eDisposition.18">
        <div className="fgrid">
          <div className="fcol-12">
            <AutoField label="Reason for Patient Transport (Medical Necessity)" elem="eDisposition.18" />
          </div>
          <div className="fcol-12">
            <TextField label="Documentation Supporting Necessity" path="eBilling/eBilling.NecessityNotes" multiline rows={3} />
          </div>
        </div>
      </Section>

      <Section id="transport" num="05" title="Transport" meta="codes & modifiers">
        <div className="fgrid">
          <div className="fcol-4"><TextField label="Origin Code" path="eBilling/eBilling.OriginCode" /></div>
          <div className="fcol-4"><TextField label="Destination Code" path="eBilling/eBilling.DestCode" /></div>
          <div className="fcol-4"><TextField label="Modifier" path="eBilling/eBilling.Modifier" /></div>
        </div>
      </Section>

      <Section id="work" num="06" title="Work Related" meta="injury context">
        <div className="fgrid">
          <div className="fcol-6"><OptionField label="Work Related?" elem="eScene.WorkRelated" path="eScene/eScene.WorkRelated" /></div>
          <div className="fcol-6"><TextField label="Employer" path="eScene/eScene.Employer" /></div>
        </div>
      </Section>

      <Section id="nok" num="07" title="Next of Kin" meta="contact info">
        <div className="fgrid">
          <div className="fcol-4"><TextField label="Last Name" path="ePatient/ePatient.NOK.Last" /></div>
          <div className="fcol-4"><TextField label="First Name" path="ePatient/ePatient.NOK.First" /></div>
          <div className="fcol-4"><TextField label="Relationship" path="ePatient/ePatient.NOK.Relation" /></div>
          <div className="fcol-6"><TextField label="Phone" path="ePatient/ePatient.NOK.Phone" /></div>
          <div className="fcol-6"><TextField label="Email" path="ePatient/ePatient.NOK.Email" /></div>
        </div>
      </Section>

      <Section id="consumables" num="08" title="Consumables" meta="—">
        <ConsumablesList />
      </Section>
    </ViewLayout>
  );
}

function ConsumablesList() {
  return (
    <div style={{ border: "1px solid var(--epcr-line)", borderRadius: 6, overflow: "hidden" }}>
      <div style={{ padding: 24, textAlign: "center", color: "var(--epcr-ink-4)" }}>
        No consumables recorded
      </div>
      <div style={{ padding: "12px 14px", display: "flex", gap: 8, borderTop: "1px solid var(--epcr-line-2)" }}>
        <button className="btn-secondary"><Icons.plus size={14} /> Add consumable</button>
      </div>
    </div>
  );
}

export function EpcrViewSignatures() {
  const data = useData();
  const sigs = useField("eOther/eOther.SignatureGroup") || [];
  const list = Array.isArray(sigs) ? sigs : (sigs ? [sigs] : []);

  const sections = list.map((s, i) => ({
    id: `sig-${i + 1}`,
    label: `${data.label("eOther.12", s["eOther.12"]) || "Signature"} ${i + 1}`,
  }));

  return (
    <ViewLayout sections={sections}>
      {list.map((s, i) => (
        <Section
          key={i}
          id={`sig-${i + 1}`}
          num={String(i + 1).padStart(2, "0")}
          title={data.label("eOther.12", s["eOther.12"]) || "Signature"}
          meta={data.label("eOther.15", s["eOther.15"]) || ""}
        >
          <SigCard
            role={data.label("eOther.12", s["eOther.12"])}
            type={data.label("eOther.13", s["eOther.13"])}
            documentType={data.label("eOther.15", s["eOther.15"])}
            name={[s["eOther.20"], s["eOther.21"]].filter(Boolean).join(" ")}
            time={s["eOther.19"]}
            signed
          />
        </Section>
      ))}
      {list.length === 0 && (
        <div style={{ padding: 60, textAlign: "center", color: "var(--epcr-ink-4)" }}>
          No signatures recorded
        </div>
      )}
    </ViewLayout>
  );
}

function SigCard({ role, name, time, type, documentType, signed }) {
  return (
    <div className="sig-card">
      <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
        <div>
          <div className="sig-role">{role || "—"}</div>
          <div className="sig-name">{name || "—"}</div>
        </div>
        <span className={"status-pill " + (signed ? "" : "draft")} style={{ marginLeft: "auto" }}>
          <span className="dt" /> {signed ? (type || "Collected") : "Awaiting signature"}
        </span>
      </div>
      <div className={"sig-canvas " + (signed ? "signed" : "")} style={{ fontFamily: "'IBM Plex Mono', monospace", color: "var(--epcr-ink-3)" }}>
        {signed ? `${name || "—"} · ${type || "—"}` : "Tap or click to sign"}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {time && <div className="sig-meta">{time}</div>}
        {documentType && <div className="sig-meta">· {documentType}</div>}
        <button className="btn-ghost" style={{ marginLeft: "auto" }}>
          {signed ? "Re-sign" : "Capture signature"}
        </button>
      </div>
    </div>
  );
}
